// Dateien-Tab der Online-Marketing-Suite: Austauschordner zwischen Uli und Claude.
//
// Lag urspruenglich als wireFilesTab() in client.ts und wurde nur aus dem
// Config-Modal heraus verdrahtet — das Panel sitzt aber im Marketing-Modal.
// Dadurch wurde reload() nie aufgerufen und der Platzhalter "Wird geladen …"
// blieb dauerhaft stehen. Jetzt als eigenes init*Tab-Modul wie die anderen Tabs.
import type { MarketingDeps } from "./marketing.js";

interface FileRow { name: string; bytes: number; modified: string }

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Projekt-Slug aus dem Kopf-Dropdown. Defensiv: das Element kann fehlen. */
function currentSlug(): string | null {
  const sel = document.getElementById("project-select") as HTMLSelectElement | null;
  return sel?.selectedOptions?.[0]?.value ?? null;
}

/** Für die Suche: klein, Umlaute wie in den Dateinamen (ä → ae …), Trennzeichen als Leerzeichen. */
function norm(s: string): string {
  return s.toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[_\-.,;:()\[\]]+/g, " ").replace(/\s+/g, " ").trim();
}

/** Ein Suchwort wie „16.10.“ oder „16.10.2026“ wird zu „10 16“ bzw. „2026 10 16“ (Schreibweise der Dateinamen). */
function suchwort(w: string): string {
  const d = w.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{2,4})?$/);
  if (d) {
    const tag = d[1]!.padStart(2, "0"), monat = d[2]!.padStart(2, "0");
    const jahr = d[3] ? (d[3].length === 2 ? `20${d[3]}` : d[3]) : "";
    return jahr ? `${jahr} ${monat} ${tag}` : `${monat} ${tag}`;
  }
  return norm(w);
}

/** Suchtext je Datei: Name, Name ohne Leerzeichen („hotstone“ findet „hot-stone“) und Änderungsdatum. */
function heuhaufen(f: FileRow): string {
  const n = norm(f.name);
  return `${n} | ${n.replace(/ /g, "")} | ${f.modified.slice(0, 10).replace(/-/g, " ")}`;
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

export function initFilesTab(m: Element, deps: MarketingDeps): void {
  const { api } = deps;
  const list = m.querySelector("[data-files-list]") as HTMLElement | null;
  const msg = m.querySelector("[data-files-msg]") as HTMLElement | null;
  const input = m.querySelector("[data-files-input]") as HTMLInputElement | null;
  if (!list) return;

  // 19.10.2026 (Uli): Freitextsuche. Gefiltert wird im Browser, die Liste kommt wie bisher komplett.
  // Die Suchzeile entsteht hier statt in app.html: app.html liest der Server nur beim Start ein,
  // client.js bei jedem Aufruf. So wirkt eine Änderung ohne Neustart der Suite.
  const zeile = document.createElement("div");
  zeile.className = "om-files-filter";
  zeile.innerHTML = '<input type="search" data-files-search placeholder="Dateien suchen, z. B. „aiko frank“, „awin hot stone“ oder „16.10.“" aria-label="Dateien suchen" autocomplete="off">'
    + '<span data-files-count class="dim"></span>';
  list.before(zeile);
  const search = zeile.querySelector("[data-files-search]") as HTMLInputElement | null;
  const count = zeile.querySelector("[data-files-count]") as HTMLElement | null;
  let files: FileRow[] = [];
  let slug = "";

  const render = (): void => {
    const woerter = (search?.value ?? "").trim().split(/\s+/).filter(Boolean).map(suchwort).filter(Boolean);
    const treffer = woerter.length
      ? files.filter((f) => { const h = heuhaufen(f); return woerter.every((w) => h.includes(w)); })
      : files;
    if (count) {
      const fmt = (n: number): string => n.toLocaleString("de-DE");
      count.textContent = woerter.length ? `${fmt(treffer.length)} von ${fmt(files.length)} Dateien` : `${fmt(files.length)} Dateien`;
    }
    if (!treffer.length) {
      list.innerHTML = '<p class="dim">Keine Datei passt zur Suche.</p>';
      return;
    }
    const rows = treffer.map((f) => {
      const url = `/api/projects/${encodeURIComponent(slug)}/files/${encodeURIComponent(f.name)}`;
      const when = new Date(f.modified).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
      return `<tr>
        <td><a href="${url}" target="_blank" rel="noopener">${escapeHtml(f.name)}</a></td>
        <td class="dim" style="white-space:nowrap">${fmtBytes(f.bytes)}</td>
        <td class="dim" style="white-space:nowrap">${when}</td>
        <td style="text-align:right"><button class="btn-secondary" data-file-del="${escapeHtml(f.name)}" title="Löschen">✕</button></td>
      </tr>`;
    }).join("");
    list.innerHTML = `<table class="om-table"><thead><tr><th>Datei</th><th>Größe</th><th>Geändert</th><th></th></tr></thead><tbody>${rows}</tbody></table>`;
    list.querySelectorAll<HTMLButtonElement>("[data-file-del]").forEach((b) => {
      b.addEventListener("click", async () => {
        const name = b.dataset.fileDel!;
        if (!confirm(`"${name}" wirklich löschen?`)) return;
        // Die Marketing-api() kennt kein DELETE -> direkt per fetch, mit CSRF.
        const csrf = getCookie("csrf_token");
        await fetch(`/api/projects/${encodeURIComponent(slug)}/files/${encodeURIComponent(name)}`, {
          method: "DELETE", headers: csrf ? { "x-csrf-token": csrf } : {},
        });
        void reload();
      });
    });
  };

  const reload = async (): Promise<void> => {
    try {
      const s = currentSlug();
      if (!s) { list.innerHTML = '<p class="dim">Kein Projekt ausgewählt.</p>'; if (count) count.textContent = ""; return; }
      slug = s;
      const r = await api<{ files: FileRow[] }>(`/api/projects/${encodeURIComponent(slug)}/files`);
      if (r.status !== 200 || !r.data) {
        list.innerHTML = `<p class="dim">Konnte nicht geladen werden (HTTP ${r.status}).</p>`;
        return;
      }
      files = r.data.files;
      if (!files.length) {
        list.innerHTML = '<p class="dim">Noch keine Dateien. Lade eine hoch oder lass Claude eine ablegen.</p>';
        if (count) count.textContent = "";
        return;
      }
      render();
    } catch (e) {
      // Ohne diesen Fang bleibt bei jedem Fehler stumm "Wird geladen …" stehen.
      list.innerHTML = `<p class="dim">Fehler beim Laden: ${escapeHtml(String(e))}</p>`;
    }
  };

  search?.addEventListener("input", render);
  search?.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && search.value) { e.preventDefault(); e.stopPropagation(); search.value = ""; render(); }
  });

  (m.querySelector('[data-action="files-reload"]') as HTMLButtonElement | null)
    ?.addEventListener("click", () => { void reload(); });

  input?.addEventListener("change", async () => {
    const slug = currentSlug();
    if (!slug || !input.files?.length) return;
    for (const file of Array.from(input.files)) {
      if (msg) msg.textContent = `Lade ${file.name} …`;
      const buf = await file.arrayBuffer();
      const csrf = getCookie("csrf_token");
      const r = await fetch(`/api/projects/${encodeURIComponent(slug)}/files?name=${encodeURIComponent(file.name)}`, {
        method: "POST",
        headers: { "content-type": "application/octet-stream", ...(csrf ? { "x-csrf-token": csrf } : {}) },
        body: buf,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({ error: String(r.status) }));
        if (msg) msg.textContent = `${file.name}: ${e.error === "bad_filename" ? "Dateityp nicht erlaubt" : e.error}`;
        continue;
      }
    }
    if (msg) msg.textContent = "Fertig.";
    input.value = "";
    void reload();
    setTimeout(() => { if (msg) msg.textContent = ""; }, 4000);
  });

  // Beim Tab-Wechsel neu laden — der Ordner aendert sich waehrend der Sitzung.
  m.querySelectorAll<HTMLButtonElement>('.tab-btn[data-tab="dateien"]').forEach((b) => {
    b.addEventListener("click", () => { void reload(); setTimeout(() => search?.focus(), 0); });
  });

  void reload();
}
